import React, { useCallback, useState } from 'react';
import { Icon } from '../icon/Icon';
import { IconName } from '../icon/IconName';
import { Checkbox } from '../form/checkbox/Checkbox';
import { Radio } from '../form/radio/Radio';
import {
	AccordionWrapper,
	AccordionHeader,
	HeaderLeft,
	HeaderRight,
	Title,
	ChevronWrapper,
	ExtraIconWrapper,
	CheckboxWrapper,
	AccordionContentWrapper,
	AccordionContent,
	AccordionInnerContent,
	Divider,
} from './Accordion.styled';
import { useUniqueId } from '../utils/useUniqueId';

type BaseAccordionProps = {
	title: string;
	children: React.ReactNode;
	isOpen?: boolean;
	defaultOpen?: boolean;
	onToggle?: (isOpen: boolean) => void;
	className?: string;

	hideChevron?: boolean;
	iconName?: IconName;
	size?: 'small' | 'large';
	scrollable?: boolean;
};

type WithCheckbox = {
	checkbox: true;
	checkboxChecked?: boolean;
	onCheckboxChange?: (checked: boolean) => void;

	radioButton?: never;
	radioButtonChecked?: never;
	onRadioButtonChange?: never;
};

type WithRadio = {
	radioButton: true;
	radioButtonChecked?: boolean;
	onRadioButtonChange?: (checked: boolean) => void;

	checkbox?: never;
	checkboxChecked?: never;
	onCheckboxChange?: never;
};

type WithNoControl = {
	checkbox?: false | undefined;
	radioButton?: false | undefined;

	checkboxChecked?: never;
	onCheckboxChange?: never;
	radioButtonChecked?: never;
	onRadioButtonChange?: never;
};

export type AccordionProps = BaseAccordionProps & (WithCheckbox | WithRadio | WithNoControl);

export const Accordion = ({
	title,
	children,
	isOpen: controlledIsOpen,
	defaultOpen = false,
	onToggle,
	className,

	checkbox,
	checkboxChecked,
	onCheckboxChange,

	radioButton,
	radioButtonChecked,
	onRadioButtonChange,

	hideChevron = false,
	iconName,
	size = 'large',
	scrollable = false
}: AccordionProps) => {
	const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

	const isControlled = controlledIsOpen !== undefined;
	const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

	const id = useUniqueId('accordion');
	const HEADER_ID = `accordion-header-${id}`;
	const CONTENT_ID = `accordion-content-${id}`;
	const RADIO_ID = `accordion-radio-${id}`;

	const handleToggle = useCallback(() => {
		const newState = !isOpen;
		if (!isControlled) setInternalIsOpen(newState);
		onToggle?.(newState);
	}, [isOpen, isControlled, onToggle]);

	const handleHeaderKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === ' ') {
				e.preventDefault();
				handleToggle();
			}
		},
		[handleToggle]
	);

	const stopHeaderToggle = (e: React.SyntheticEvent) => {
		e.stopPropagation();
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onCheckboxChange?.(e.target.checked);
	};

	const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onRadioButtonChange?.(e.target.checked);
	};

	return (
		<AccordionWrapper $size={size} className={className}>
			<AccordionHeader
				onClick={handleToggle}
				onKeyDown={handleHeaderKeyDown}
				tabIndex={0}
				$isOpen={isOpen}
				role="button"
				aria-expanded={isOpen}
				aria-controls={CONTENT_ID}
				id={HEADER_ID}
			>
				<HeaderLeft>
					{radioButton && (
						<CheckboxWrapper onClick={stopHeaderToggle} onKeyDown={stopHeaderToggle}>
							<Radio
								id={RADIO_ID}
								checked={!!radioButtonChecked}
								onChange={handleRadioChange}
								onClick={stopHeaderToggle}
								onKeyDown={(e) => {
									if (e.key === ' ') stopHeaderToggle(e);
								}}
								aria-label="Select"
							/>
						</CheckboxWrapper>
					)}

					{checkbox && (
						<CheckboxWrapper onClick={stopHeaderToggle} onKeyDown={stopHeaderToggle}>
							<Checkbox checked={checkboxChecked} onChange={handleCheckboxChange} />
						</CheckboxWrapper>
					)}

					<Title $size={size}>{title}</Title>

					{iconName && (
						<ExtraIconWrapper>
							<Icon icon={iconName} />
						</ExtraIconWrapper>
					)}
				</HeaderLeft>

				{!hideChevron && (
					<HeaderRight>
						<ChevronWrapper $isOpen={isOpen}>
							<Icon icon="chevron-down" />
						</ChevronWrapper>
					</HeaderRight>
				)}
			</AccordionHeader>

			<AccordionContentWrapper
				$isOpen={isOpen}
				id={CONTENT_ID}
				role="region"
				aria-labelledby={HEADER_ID}
			>
				<AccordionContent>
					{size === 'large' && <Divider />}
					<AccordionInnerContent $scrollable={scrollable}>{children}</AccordionInnerContent>
				</AccordionContent>
			</AccordionContentWrapper>
		</AccordionWrapper>
	);
};
